import React, { useEffect, useState } from 'react'
import { apiClient } from '@/utils/api'
import { AlertCircle, X } from 'lucide-react'

interface SubscriptionStatus {
  id: string
  family_id: string
  product: string
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid'
  billing_cycle: 'monthly' | 'yearly'
  current_period_start: string
  current_period_end: string
  trial_end?: string
  cancel_at_period_end: boolean
  canceled_at?: string
  stripe_subscription_id: string
  created_at: string
  updated_at: string
}

export const SubscriptionBanner: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadSubscription()
  }, [])

  const loadSubscription = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus()
      setSubscription(data)
    } catch (err) {
      // Silently fail - banner won't show
    } finally {
      setLoading(false)
    }
  }

  if (loading || dismissed || !subscription) {
    return null
  }

  const daysUntilEnd = () => {
    if (!subscription.current_period_end) return null
    const end = new Date(subscription.current_period_end)
    const now = new Date()
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    return diff
  }

  const days = daysUntilEnd()

  // Show banner for trial ending soon, past due, or canceled
  const showTrialWarning = subscription.status === 'trialing' && days !== null && days <= 7 && days > 0
  const showPastDue = subscription.status === 'past_due'
  const showCanceled = subscription.cancel_at_period_end

  if (!showTrialWarning && !showPastDue && !showCanceled) {
    return null
  }

  let message = ''
  let actionText = ''
  let actionUrl = ''
  let bgColor = ''
  let textColor = ''

  if (showPastDue) {
    message = days !== null && days >= -7 && days < 0
      ? `Payment failed. You have ${Math.abs(days)} days remaining in your grace period.`
      : 'Payment failed. Please update your payment method.'
    actionText = 'Update Payment'
    actionUrl = '/dashboard/settings'
    bgColor = 'bg-red-600'
    textColor = 'text-white'
  } else if (showTrialWarning) {
    message = `Your free trial ends in ${days} day${days === 1 ? '' : 's'}.`
    actionText = 'Subscribe Now'
    actionUrl = 'https://housepoints.ai/pricing'
    bgColor = 'bg-blue-600'
    textColor = 'text-white'
  } else if (showCanceled) {
    message = `Your subscription will end on ${new Date(subscription.current_period_end).toLocaleDateString()}.`
    actionText = 'Reactivate'
    actionUrl = '/dashboard/settings'
    bgColor = 'bg-yellow-600'
    textColor = 'text-white'
  }

  return (
    <div className={`${bgColor} ${textColor} shadow-lg`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm font-medium">{message}</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={actionUrl}
              className="bg-white text-gray-900 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-100 transition"
            >
              {actionText}
            </a>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded transition"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
